import React from "react";
import plur from "plur";
import { Button, Flex, LoadingIndicator, ErrorMessage, Link } from "@jsq/ds/core";
import { type PositionNodeType, type PositionsPaginatedType } from "../../../common/types";
import InvestmentEntityText from "../../PositionInvestmentName";
import { loadMorePostionsButton } from "./styles_vanilla.css";

export type PositionListBaseComponentProps = {
  positionsPaginated: PositionsPaginatedType | undefined;
  fetchMorePositions: () => void;
  positionListError: Error | undefined | null;
  positionListLoading: boolean;
  linkifyPosition?: (position: PositionNodeType) => string;
};

export const PositionListBaseComponent = ({
  positionsPaginated,
  fetchMorePositions,
  positionListError,
  positionListLoading,
  linkifyPosition,
}: PositionListBaseComponentProps) => {
  if (positionListLoading) {
    return <LoadingIndicator />;
  }

  if (positionListError) {
    return <ErrorMessage>Failed to load positions</ErrorMessage>;
  }

  const remainingPositionCount =
    (positionsPaginated?.count ?? 0) - (positionsPaginated?.edges.length ?? 0);
  return (
    <Flex flexDirection="column">
      {positionsPaginated?.edges.map((position) => {
        if (linkifyPosition) {
          return (
            <Link
              key={`position-${position.node.id}`}
              href={linkifyPosition(position.node)}
              shouldOpenInNewTab
              align="left"
            >
              {position.node.investmentEntity.name}
            </Link>
          );
        }
        return (
          <InvestmentEntityText
            key={`position-${position.node.id}`}
            investmentEntityName={position.node.investmentEntity.name}
          />
        );
      })}
      {positionsPaginated?.pageInfo.hasNextPage && (
        <Button
          variant="link"
          displayAsLink
          onClick={fetchMorePositions}
          data-testid="load-more-positions"
          id="load-more-positions-button"
          className={loadMorePostionsButton}
        >
          {`and ${remainingPositionCount} other ${plur("position", remainingPositionCount)}`}
        </Button>
      )}
    </Flex>
  );
};
